import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { getSquaresForCell } from '../../types';
import type { GridProps } from './types';
import {
  getContrastColor,
  getMergedGridDimensions,
  getCellNumberForVisualPosition,
  GRID_CONSTANTS,
} from './gridUtils';
import {
  isSquareClickable,
  getSquareLabel,
  getSquareTooltip,
  HitBadge,
  BadNumberBadge,
  CellContent,
  GridCell,
  MaybeTooltip,
} from './gridHelpers';

const { cellSize, headerSize, gap } = GRID_CONSTANTS;

/**
 * MergedCellGrid renders the grid for paired/quad cell modes.
 * Handles merged cell rendering with combined hit counts, scaled icons, and merged number headers.
 */
export const MergedCellGrid: React.FC<GridProps> = ({
  board,
  grid,
  themeData,
  cellMode,
  cellShape,
  topNumbers,
  leftNumbers,
  topColor,
  leftColor,
  topAccent,
  leftAccent,
  numberSetKeys,
  hasMultipleNumberSets,
  getTopNumbersForKey,
  getLeftNumbersForKey,
  isActiveNumberSetKey,
  getHitCount,
  isBadNumberSquare,
  currentUserId,
  isManager,
  approvedSquares,
  userClaimedCount,
  highlightedCells,
  assignModeEnabled,
  assignModePlayerId,
  assignModeAtLimit,
  hideNames,
  onSquareClick,
  onClearSquare,
  hoveredCell,
  onCellMouseEnter,
  onCellMouseLeave,
  leftTeam,
  leftTeamType,
  gridHeight,
  leftTeamLogoUrl,
}) => {
  const gridDimensions = useMemo(
    () => getMergedGridDimensions(cellMode, cellShape),
    [cellMode, cellShape]
  );
  const { rows: visualRows, cols: visualCols, rowSpan, colSpan } = gridDimensions;
  const mergedCellHeight = cellSize * rowSpan + (rowSpan - 1) * gap;
  const mergedCellWidth = cellSize * colSpan + (colSpan - 1) * gap;

  const iconSize = cellMode === 'quad' ? 36 : 28;

  const getSquareColor = (square: typeof grid[0][0] | undefined, row: number, col: number, cellHitCount: number) => {
    if (cellHitCount > 0) return themeData.squareWinner;
    if (!square) return themeData.squareUnclaimed;
    if (assignModeEnabled && assignModePlayerId) {
      if (square.ownerId === assignModePlayerId || square.offlinePlayerId === assignModePlayerId) {
        return themeData.squareSelected;
      }
    }
    if (square.ownerId) {
      return square.ownerId === currentUserId ? themeData.squareClaimedSelf : themeData.squareClaimedOther;
    }
    if (square.offlinePlayerId) return themeData.squareOfflinePlayer;
    return themeData.squareUnclaimed;
  };

  /**
   * Render merged number header cells for a row of numbers (top axis).
   */
  const renderMergedTopNumbers = (numbers: number[], isActive: boolean = true) => (
    Array.from({ length: visualCols }, (_, visualColIndex) => {
      const actualCol = visualColIndex * colSpan;
      const isHighlighted = hoveredCell && Math.floor(hoveredCell.col / colSpan) === visualColIndex && isActive;
      const numbersInCell = Array.from({ length: colSpan }, (_, i) => numbers[actualCol + i]);
      return (
        <Box
          key={visualColIndex}
          sx={{
            width: mergedCellWidth,
            height: headerSize,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            ml: 0.5,
            opacity: isActive ? 1 : 0.5,
          }}
        >
          {board.numbersRevealed ? (
            numbersInCell.map((num, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isHighlighted ? topColor : (isActive ? themeData.headerBackground : '#f5f5f5'),
                  border: `1px solid ${themeData.cellBorder}`,
                  borderRight: i < numbersInCell.length - 1 ? 'none' : undefined,
                  borderLeft: i > 0 ? 'none' : undefined,
                  color: isHighlighted ? topAccent : (isActive ? topColor : '#999'),
                  fontWeight: themeData.headerFontWeight,
                  fontFamily: themeData.fontFamily,
                  fontSize: '0.85rem',
                  transition: 'background-color 0.15s, color 0.15s, opacity 0.15s',
                }}
              >
                {num}
              </Box>
            ))
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isActive ? themeData.headerBackground : '#f5f5f5',
                border: `1px solid ${themeData.cellBorder}`,
                color: isActive ? topColor : '#999',
                fontWeight: themeData.headerFontWeight,
                fontFamily: themeData.fontFamily,
              }}
            >
              -
            </Box>
          )}
        </Box>
      );
    })
  );

  /**
   * Render merged number header cells for a column of numbers (left axis).
   */
  const renderMergedLeftNumbers = (numbers: number[], isActive: boolean = true) => (
    Array.from({ length: visualRows }, (_, visualRowIndex) => {
      const actualRow = visualRowIndex * rowSpan;
      const isHighlighted = hoveredCell && Math.floor(hoveredCell.row / rowSpan) === visualRowIndex && isActive;
      const numbersInCell = Array.from({ length: rowSpan }, (_, i) => numbers[actualRow + i]);
      return (
        <Box
          key={visualRowIndex}
          sx={{
            width: headerSize,
            height: mergedCellHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            mb: 0.5,
            opacity: isActive ? 1 : 0.5,
          }}
        >
          {board.numbersRevealed ? (
            numbersInCell.map((num, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isHighlighted ? leftColor : (isActive ? themeData.headerBackground : '#f5f5f5'),
                  border: `1px solid ${themeData.cellBorder}`,
                  borderBottom: i < numbersInCell.length - 1 ? 'none' : undefined,
                  borderTop: i > 0 ? 'none' : undefined,
                  color: isHighlighted ? leftAccent : (isActive ? leftColor : '#999'),
                  fontWeight: themeData.headerFontWeight,
                  fontFamily: themeData.fontFamily,
                  fontSize: '0.85rem',
                  transition: 'background-color 0.15s, color 0.15s, opacity 0.15s',
                }}
              >
                {num}
              </Box>
            ))
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isActive ? themeData.headerBackground : '#f5f5f5',
                border: `1px solid ${themeData.cellBorder}`,
                color: isActive ? leftColor : '#999',
                fontWeight: themeData.headerFontWeight,
                fontFamily: themeData.fontFamily,
              }}
            >
              -
            </Box>
          )}
        </Box>
      );
    })
  );

  return (
    <>
      {/* Top Numbers */}
      {!hasMultipleNumberSets ? (
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          <Box sx={{ width: headerSize }} />
          <Box sx={{ width: headerSize }} />
          {renderMergedTopNumbers(topNumbers)}
        </Box>
      ) : (
        <Box sx={{ mb: 0.5 }}>
          {[...numberSetKeys].reverse().map((key) => {
            const numbersForKey = getTopNumbersForKey(key);
            const isActiveRow = isActiveNumberSetKey(key);
            return (
              <Box key={key} sx={{ display: 'flex', mb: 0.25 }}>
                <Box sx={{ width: headerSize }} />
                <Box sx={{ width: numberSetKeys.length * (headerSize + 2) }} />
                {renderMergedTopNumbers(numbersForKey, isActiveRow)}
                <Box
                  sx={{
                    width: headerSize * 0.6,
                    height: headerSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    color: isActiveRow ? topColor : '#999',
                    opacity: isActiveRow ? 1 : 0.6,
                    ml: 0.5,
                  }}
                >
                  {key}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Main Grid Row: Left Team + Left Numbers + Grid Cells */}
      <Box sx={{ display: 'flex' }}>
        {/* Left Team Label */}
        <Box
          sx={{
            width: headerSize,
            height: gridHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontWeight: 'bold',
              color: leftColor,
              whiteSpace: 'nowrap',
              fontSize: '1.1rem',
            }}
          >
            {leftTeam} ({leftTeamType})
          </Typography>
          {leftTeamLogoUrl && (
            <Box
              component="img"
              src={leftTeamLogoUrl}
              alt={leftTeam}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Left Numbers */}
        {!hasMultipleNumberSets ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {renderMergedLeftNumbers(leftNumbers)}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {[...numberSetKeys].reverse().map((key) => {
              const numbersForKey = getLeftNumbersForKey(key);
              const isActiveCol = isActiveNumberSetKey(key);
              return (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', mr: 0.25 }}>
                  <Box
                    sx={{
                      width: headerSize,
                      height: headerSize * 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      color: isActiveCol ? leftColor : '#999',
                      opacity: isActiveCol ? 1 : 0.6,
                      mb: 0.25,
                    }}
                  >
                    {key}
                  </Box>
                  {renderMergedLeftNumbers(numbersForKey, isActiveCol)}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Grid Cells */}
        <Box onMouseLeave={onCellMouseLeave} data-testid="squares-grid">
          {hasMultipleNumberSets && (
            <Box sx={{ height: headerSize * 0.5 + 2, mb: 0.25 }} />
          )}
          {Array.from({ length: visualRows }, (_, visualRowIndex) => (
            <Box key={visualRowIndex} sx={{ display: 'flex', mb: 0.5 }}>
              {Array.from({ length: visualCols }, (_, visualColIndex) => {
                const cellNumber = getCellNumberForVisualPosition(visualRowIndex, visualColIndex, cellMode, cellShape);
                const cellSquarePositions = getSquaresForCell(cellNumber, cellMode, cellShape);
                const firstPos = cellSquarePositions[0];
                const square = grid[firstPos[0]]?.[firstPos[1]];

                const cellHitCount = cellSquarePositions.reduce(
                  (sum, pos) => sum + getHitCount(pos[0], pos[1]), 0
                );
                const hasBadSquare = cellSquarePositions.some(
                  pos => isBadNumberSquare(pos[0], pos[1])
                );
                const isHighlightedFromGameLog = cellSquarePositions.some(pos =>
                  highlightedCells?.some(cell => cell.row === pos[0] && cell.col === pos[1])
                ) ?? false;
                const hasActiveHighlight = highlightedCells && highlightedCells.length > 0;
                const isDimmed = hasActiveHighlight && !isHighlightedFromGameLog;

                const clickable = isSquareClickable(
                  square, board.status, isManager, currentUserId,
                  assignModeEnabled, assignModePlayerId, assignModeAtLimit,
                  approvedSquares, userClaimedCount, onSquareClick, onClearSquare,
                );

                const bgColor = getSquareColor(square, firstPos[0], firstPos[1], cellHitCount);
                const textColor = getContrastColor(bgColor);
                const tooltipText = getSquareTooltip(square, cellHitCount, hideNames);
                const { icon, handle } = getSquareLabel(square, cellNumber.toString(), hideNames, iconSize, true);

                const cellContent = (
                  <GridCell
                    width={mergedCellWidth}
                    height={mergedCellHeight}
                    bgColor={bgColor}
                    isClickable={clickable}
                    isHighlighted={isHighlightedFromGameLog}
                    isDimmed={!!isDimmed}
                    highlightScale="1.05"
                    hoverScale="1.02"
                    onClick={() => onSquareClick(firstPos[0], firstPos[1])}
                    onMouseEnter={() => onCellMouseEnter(firstPos[0], firstPos[1])}
                  >
                    <HitBadge count={cellHitCount} themeData={themeData} size="large" />
                    <BadNumberBadge isBad={hasBadSquare} size="large" />
                    <CellContent
                      icon={icon}
                      handle={handle}
                      textColor={textColor}
                      gap={cellMode === 'quad' ? 1 : 0.5}
                      fontSize={{
                        withIcon: cellMode === 'quad' ? '0.85rem' : '0.7rem',
                        withoutIcon: cellMode === 'quad' ? '1rem' : '0.85rem',
                      }}
                    />
                  </GridCell>
                );

                return (
                  <MaybeTooltip key={visualColIndex} tooltipText={tooltipText} reactKey={visualColIndex}>
                    {cellContent}
                  </MaybeTooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};
